import React, { useEffect, useState } from 'react';
import { Maximize2, Check, X } from 'lucide-react';
import { useFreehand } from '@/lib/freehandStore';
import { Slider } from '@/components/ui/slider';

const QUICK = [90, 95, 105, 110];
const PIVOTS = ['top', 'bottom', 'front', 'back', 'left', 'right'];

export const FreehandScaleAll = ({ panelCount }) => {
  const previewScaleAll = useFreehand((s) => s.previewScaleAll);
  const commitScaleAll = useFreehand((s) => s.commitScaleAll);
  const cancelScaleAll = useFreehand((s) => s.cancelScaleAll);
  const scaleAll = useFreehand((s) => s.scaleAll);
  const previewing = useFreehand((s) => !!s.scaleAllSnapshot);
  const pivot = useFreehand((s) => s.scalePivot);
  const setPivot = useFreehand((s) => s.setScalePivot);
  const setMarker = useFreehand((s) => s.setScaleMarkerVisible);
  const [pct, setPct] = useState(100);

  useEffect(() => { if (!previewing) setPct(100); }, [previewing]);
  useEffect(() => () => { const st = useFreehand.getState(); st.commitScaleAll(); st.setScaleMarkerVisible(false); }, []);

  const onSlide = ([v]) => { setPct(v); previewScaleAll(v / 100); };
  const btn = 'h-7 rounded-lg border border-slate-200 bg-white text-[11px] font-mono text-slate-700 hover:border-slate-900 hover:bg-slate-50 disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white transition-colors';

  return (
    <div
      className="space-y-2 pt-3 border-t border-slate-200"
      data-testid="freehand-scale-all"
      onMouseEnter={() => setMarker(true)}
      onMouseLeave={() => setMarker(false)}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <Maximize2 className="w-3 h-3" /> Scale all panels
        </div>
        <div className="text-[10px] font-mono text-slate-500" data-testid="freehand-scale-all-value">{pct}%</div>
      </div>
      <div className="text-[11px] text-slate-500 leading-snug">
        Spread the whole layout out from the chosen side of the ball, or pull it in toward it. Undoable.
      </div>
      <div className="grid grid-cols-6 gap-1" data-testid="freehand-scale-pivot">
        {PIVOTS.map((id) => (
          <button
            key={id}
            onClick={() => setPivot(id)}
            data-testid={`freehand-scale-pivot-${id}`}
            className={`h-6 rounded-md border text-[10px] capitalize transition-colors ${pivot === id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-900'}`}
          >
            {id}
          </button>
        ))}
      </div>
      <Slider
        min={50} max={150} step={1} value={[pct]}
        disabled={panelCount === 0}
        onValueChange={onSlide}
        data-testid="freehand-scale-all-slider"
      />
      {previewing ? (
        <div className="grid grid-cols-2 gap-1.5">
          <button onClick={commitScaleAll} data-testid="freehand-scale-all-apply" className={`${btn} flex items-center justify-center gap-1 !bg-slate-900 !text-white !border-slate-900`}>
            <Check className="w-3 h-3" /> Apply
          </button>
          <button onClick={cancelScaleAll} data-testid="freehand-scale-all-cancel" className={`${btn} flex items-center justify-center gap-1`}>
            <X className="w-3 h-3" /> Cancel
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-1.5">
          {QUICK.map((q) => (
            <button key={q} disabled={panelCount === 0} onClick={() => scaleAll(q / 100)} data-testid={`freehand-scale-all-${q}`} className={btn}>
              {q}%
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
